import {
  Button,
  ButtonColor,
  Modal,
  type ModalHook,
  TakeoverModalContent,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import { type FC } from 'react';
import { useForm } from 'react-hook-form';

import LoadingIndicator from '../common/LoadingIndicator';
import Input from '../inputs/Input';

interface GuestSignupModalHookProps {
  onSignup: (vals: GuestSignupFormValues) => Promise<void>;
  onLoginClick: () => void;
  showLoadingIndicator: boolean;
}

interface GuestSignupModalProps extends GuestSignupModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface GuestSignupFormValues {
  email: string;
  firstName: string;
  lastName: string;
}

const REGISTER_OPTIONS = Object.freeze({
  FIRST_NAME: {
    required: {
      value: true,
      message: 'First name is required',
    },
  },
  LAST_NAME: {
    required: {
      value: true,
      message: 'Last name is required',
    },
  },
  EMAIL: {
    required: {
      value: true,
      message: 'Email is required',
    },
  },
});

const GuestSignupModalContent: FC<GuestSignupModalProps> = ({
  open,
  setOpen,
  onLoginClick,
  onSignup,
  showLoadingIndicator,
  ...modalContentProps
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<GuestSignupFormValues>();

  const onSubmit = async (data: GuestSignupFormValues) => {
    await onSignup(data);
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <TakeoverModalContent {...modalContentProps}>
        {showLoadingIndicator ? (
          <LoadingIndicator iconClassName="w-20 h-20" text="Registering..." />
        ) : (
          <form
            className="flex flex-col gap-6 p-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            <h1 className="text-3xl font-medium">Enter your info</h1>
            <div className="space-y-3">
              <div className="flex w-full flex-row gap-3 text-leland-gray-light">
                <Input
                  label="First name"
                  type="text"
                  id="firstName"
                  placeholder="First name"
                  labelIsHidden
                  className="w-full  text-leland-gray-light"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  register={register('firstName', REGISTER_OPTIONS.FIRST_NAME)}
                  error={errors.firstName}
                />
                <Input
                  label="Last name"
                  type="text"
                  id="lastName"
                  placeholder="Last name"
                  labelIsHidden
                  className="w-full  text-leland-gray-light"
                  register={register('lastName', REGISTER_OPTIONS.LAST_NAME)}
                  error={errors.lastName}
                />
              </div>
              <Input
                label="Email"
                type="email"
                id="email"
                placeholder="Email Address"
                labelIsHidden
                register={register('email', REGISTER_OPTIONS.EMAIL)}
                error={errors.email}
              />
            </div>
            <Button
              label={isSubmitting ? 'Registering...' : 'Register for free'}
              buttonColor={ButtonColor.BLACK}
              type="submit"
              disabled={isSubmitting}
              id="guest_signup_modal:register"
            />
            <p className="text-leland-gray-light">
              Already have an account?{' '}
              <button
                onClick={onLoginClick}
                className="cursor-pointer underline"
                id="guest_signup_modal:login"
              >
                Log in
              </button>
            </p>
          </form>
        )}
      </TakeoverModalContent>
    </Modal>
  );
};

export const useGuestSignupModal: ModalHook<GuestSignupModalHookProps> = () => {
  const [open, setOpen] = useControlledModalState();
  const GuestSignupModal: FC<GuestSignupModalHookProps> = (props) => {
    return open ? (
      <GuestSignupModalContent {...props} open={open} setOpen={setOpen} />
    ) : null;
  };
  return { open, setOpen, Modal: GuestSignupModal };
};
